import { VehicleRunningTotalDTO } from 'src/app/base/models/models.swagger.generated';

export const MODULE_DISPLAY_ORDER: string[] = ['TripAnalytics', 'FuelAnalytics', 'EngineAnalytics'];

const MODULE_LABEL_KEYS: Record<string, string> = {
  TripAnalytics: 'i18n.domain.analytics.running-totals.modules.trip-analytics',
  FuelAnalytics: 'i18n.domain.analytics.running-totals.modules.fuel-analytics',
  EngineAnalytics: 'i18n.domain.analytics.running-totals.modules.engine-analytics',
};

export interface ModuleLabel {
  key: string;
  params?: Record<string, string>;
}

export function moduleLabel(moduleName: string): ModuleLabel {
  const key = MODULE_LABEL_KEYS[moduleName];
  return key
    ? { key }
    : { key: 'i18n.domain.analytics.running-totals.modules.unknown', params: { name: moduleName } };
}

export interface GaugeConfig {
  referenceMax: number;
}

export interface StatDefinition {
  labelKey: string;
  valueKey: string;
  unit: string;
  decimals: number;
  extraKey?: string;
  extraLabelKey?: string;
  // Illustrative reference max for the gauge fill, not a hard data limit — same convention
  // as REFERENCE_MAX_SPEED_KMH in trip-detail-dialog.component.ts.
  gauge?: GaugeConfig;
}

const STAT_DEFINITIONS: Record<string, StatDefinition> = {
  DistanceTravelled: {
    labelKey: 'i18n.domain.analytics.running-totals.stats.distance-travelled',
    valueKey: 'distanceKm',
    unit: 'km',
    decimals: 1,
  },
  MaximumSpeed: {
    labelKey: 'i18n.domain.analytics.running-totals.stats.maximum-speed',
    valueKey: 'maxSpeedKmh',
    unit: 'km/h',
    decimals: 0,
    gauge: { referenceMax: 160 },
  },
  TotalFuelUsed: {
    labelKey: 'i18n.domain.analytics.running-totals.stats.total-fuel-used',
    valueKey: 'fuelUsedL',
    unit: 'L',
    decimals: 1,
  },
  EstimatedCo2Emissions: {
    labelKey: 'i18n.domain.analytics.running-totals.stats.estimated-co2-emissions',
    valueKey: 'co2Kg',
    unit: 'kg',
    decimals: 1,
    extraKey: 'fuelType',
    extraLabelKey: 'i18n.domain.analytics.running-totals.stats.fuel-type',
  },
  EngineRuntime: {
    labelKey: 'i18n.domain.analytics.running-totals.stats.engine-runtime',
    valueKey: 'runtimeMin',
    unit: 'min',
    decimals: 0,
  },
  MaximumRpm: {
    labelKey: 'i18n.domain.analytics.running-totals.stats.maximum-rpm',
    valueKey: 'maxRpm',
    unit: 'rpm',
    decimals: 0,
    gauge: { referenceMax: 8000 },
  },
};

export interface ParsedStat {
  statisticName: string;
  moduleName: string;
  updatedUtc: VehicleRunningTotalDTO['updatedUtc'];
  definition: StatDefinition | null;
  value: number | null;
  extraValue: string | null;
  rawEntries: [string, unknown][];
}

export function parseRunningTotal(dto: VehicleRunningTotalDTO): ParsedStat | null {
  if (!dto.valueJson) {
    return null;
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(dto.valueJson);
  } catch {
    return null;
  }

  const definition = STAT_DEFINITIONS[dto.statisticName] ?? null;
  const value = definition ? Number(parsed[definition.valueKey]) : null;
  const extraValue = definition?.extraKey ? String(parsed[definition.extraKey] ?? '') : null;

  return {
    statisticName: dto.statisticName,
    moduleName: dto.moduleName,
    updatedUtc: dto.updatedUtc,
    definition,
    value: value != null && !Number.isNaN(value) ? value : null,
    extraValue: extraValue || null,
    rawEntries: Object.entries(parsed),
  };
}

export interface ModuleGroup {
  moduleName: string;
  label: ModuleLabel;
  stats: ParsedStat[];
}

export function groupByModule(stats: ParsedStat[]): ModuleGroup[] {
  const byModule = new Map<string, ParsedStat[]>();
  for (const stat of stats) {
    const list = byModule.get(stat.moduleName) ?? [];
    list.push(stat);
    byModule.set(stat.moduleName, list);
  }

  const orderedModuleNames = [
    ...MODULE_DISPLAY_ORDER.filter(name => byModule.has(name)),
    ...[...byModule.keys()].filter(name => !MODULE_DISPLAY_ORDER.includes(name)),
  ];

  return orderedModuleNames.map(moduleName => ({
    moduleName,
    label: moduleLabel(moduleName),
    stats: byModule.get(moduleName)!,
  }));
}
